<template>
  <div class="report-overlay">
    <!-- Barre d'action (jamais imprimée) -->
    <div class="report-toolbar no-print">
      <span v-if="!chartsReady" class="report-prep">
        <span class="spinner-border spinner-border-sm me-2"></span>Préparation
        du rapport…
      </span>
      <template v-else>
        <button
          class="btn btn-outline-primary"
          :disabled="busy"
          @click="printReport">
          <i class="bi bi-printer me-1"></i>Imprimer
        </button>
        <button
          class="btn btn-primary"
          :disabled="busy"
          @click="downloadHtml2pdf">
          <i class="bi bi-file-earmark-pdf me-1"></i>Télécharger le PDF
        </button>
      </template>
      <button class="btn btn-outline-secondary" @click="$emit('close')">
        <i class="bi bi-x-lg me-1"></i>Fermer
      </button>
    </div>

    <!-- Document (format A4) -->
    <div class="report-doc" ref="docRef">
      <!-- Page de garde -->
      <section class="report-cover">
        <div class="report-cover-logos">
          <a
            href="https://lab.cercle-promodul.org/outils-numeriques-et-de-simulations/respire-recommandations-et-solutions-pour-linconfort-et-le-rafraichissement-lete"
            target="_blank"
            rel="noopener">
            <img
              src="@/assets/logo.png"
              alt="RESPIRE"
              class="report-cover-logo-respire" /><br />
            &RightArrow; lab.cercle-promodul.org - RESPIRE
          </a>
        </div>
        <div class="report-cover-txt">
          <h1 class="report-subtitle">
            Évaluation du confort d'été · {{ dateStr }}
          </h1>
        </div>
        <div style="margin-top: 10rem; text-align: center">
          <a
            href="https://lab.cercle-promodul.org/"
            target="_blank"
            rel="noopener"
            style="
              display: block;
              margin-top: 0.5rem;
              font-size: 0.9rem;
              color: #666;
            ">
            <img
              src="@/assets/logo_lab.png"
              alt="Le LAB"
              class="report-cover-logo-lab" />
            <!-- <br />
            lab.cercle-promodul.org -->
          </a>
        </div>
      </section>

      <!-- Score global -->
      <section class="report-section">
        <h2 class="report-h2">
          Niveau général d'inconfort : {{ finalScore }}%
        </h2>
        <div class="report-score">
          <MainScore
            :mainScore="finalScore"
            :roomsScore="[]"
            :isDebug="false" />
        </div>
      </section>

      <!-- Radar des sources d'inconfort -->
      <section class="report-section">
        <h2 class="report-h2">
          Origines et évaluation des sources d'inconfort
        </h2>
        <p class="report-note">
          Plus le point est éloigné du centre, plus la source d'inconfort
          associée est grande.
        </p>
        <div class="report-chart">
          <img v-if="radarPng" :src="radarPng" alt="Sources d'inconfort" />
          <div v-else class="report-chart-loading">
            Génération du graphique…
          </div>
        </div>
        <div class="report-alerts">
          <ResAlerts />
        </div>
      </section>

      <!-- Durées d'inconfort -->
      <section class="report-section">
        <h2 class="report-h2">Estimation des durées d'inconfort</h2>
        <p class="report-days">
          <template v-if="rangeNbHotDays.min === 0">
            Moins de <strong>{{ rangeNbHotDays.max }}</strong> jours d'inconfort
          </template>
          <template v-else>
            Entre
            <strong
              >{{ rangeNbHotDays.min }} et {{ rangeNbHotDays.max }}</strong
            >
            jours d'inconfort
          </template>
          <span class="report-days-sub">
            (potentiel de risque sur la période estivale)
          </span>
        </p>
        <p class="report-note">{{ hotDaysDefinition }}</p>
        <h3 class="report-h3">Répartition des jours d'inconfort</h3>
        <div class="report-chart">
          <img
            v-if="colPng"
            :src="colPng"
            alt="Répartition des jours d'inconfort" />
          <div v-else class="report-chart-loading">
            Génération du graphique…
          </div>
        </div>
      </section>

      <!-- Recommandations (liste complète, sans scroll) -->
      <section class="report-section report-reco">
        <h2 class="report-h2">
          Recommandations de travaux privilégiant les techniques de
          rafraîchissement passif
        </h2>
        <div class="report-note">
          <p v-for="(txt, i) in recoTvxIntro" :key="i">{{ txt }}</p>
          <p>
            <strong>{{ recoTvxDataNotice }}</strong>
          </p>
        </div>
        <ResReco />
      </section>

      <!-- Données d'entrée de la simulation -->
      <section class="report-section report-inputs">
        <h2 class="report-h2">Données saisies pour la simulation</h2>
        <p class="report-note">
          Récapitulatif des informations renseignées dans l'outil, sur
          lesquelles repose cette évaluation.
        </p>
        <div
          v-for="group in inputGroups"
          :key="group.title"
          class="report-input-group">
          <h3 class="report-input-title">{{ group.title }}</h3>
          <table class="report-input-table">
            <tbody>
              <tr v-for="item in group.items" :key="item.label">
                <th>{{ item.label }}</th>
                <td>{{ item.value }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <!-- Pied de page -->
      <footer class="report-footer">
        <a href="https://cercle-promodul.org" target="_blank" rel="noopener">
          <img
            src="@/assets/logo_fdcp.png"
            alt="Cercle Promodul"
            class="report-footer-logo" />
        </a>
        <p class="report-footer-text">
          <strong
            >© {{ currentYear }}
            <a href="https://cercle-promodul.org" target="_blank" rel="noopener"
              >Cercle Promodul</a
            >
            — v{{ versionString }}</strong
          ><br />
          Cet outil à vocation pédagogique, et pour l'utilisation du plus grand
          nombre, a été conçu et développé par le Fonds de Dotation Cercle
          Promodul dans le cadre d'un projet d'action soutenu par le Mécénat.
          Son utilisation est gratuite et n'est pas destinée à un usage
          commercial.
        </p>
      </footer>
    </div>

    <!-- Graphiques "live" hors écran, uniquement pour générer les PNG -->
    <div class="report-offscreen no-print" aria-hidden="true">
      <ChartRadar
        ref="radarComp"
        :dataLabels="radarLabels"
        :dataSeries="radarSeries" />
      <ChartColumns ref="colComp" :data4Chart="data4ChartQuantitatif" />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import {
  steps,
  forms,
  questions,
  hotDaysDefinition,
  recoTvxIntro,
  recoTvxDataNotice,
} from '../appConfig';
import MainScore from './charts/MainScore.vue';
import ChartRadar from './charts/ChartRadar.vue';
import ChartColumns from './charts/ChartColumns.vue';
import ResReco from './ResReco.vue';
import ResAlerts from './ResAlertes.vue';
import { matomoTrackEvent } from '@/utils/matomoTracker.js';

defineProps({
  finalScore: { type: Number, required: true },
  radarLabels: { type: Array, required: true },
  radarSeries: { type: Array, required: true },
  data4ChartQuantitatif: { type: Object, required: true },
  rangeNbHotDays: { type: Object, required: true },
});

defineEmits(['close']);

const store = useStore();
const simuData = computed(() => store.getters.simuData);

// Libellés de repli, purement cosmétiques : certaines questions construisent
// leurs choix dynamiquement (QstInsulationImprovement) et n'ont donc pas de
// `choices` dans la config. Utilisé uniquement à l'affichage du rapport, la
// valeur stockée dans le store reste inchangée.
const RAW_VALUE_LABELS = {
  none: 'Non',
  unknow: 'Je ne sais pas',
  unknown: 'Je ne sais pas',
};

function readableValue(value) {
  return RAW_VALUE_LABELS[value] ?? String(value);
}

// Traduit une valeur brute (choix) en libellé lisible via la config des questions.
function formatValue(question, value) {
  if (value === null || value === undefined || value === '') return null;
  if (Array.isArray(value)) {
    if (!value.length) return null;
    return value
      .map(
        (v) =>
          question?.choices?.find((c) => c.value === v)?.label ??
          readableValue(v),
      )
      .join(', ');
  }
  if (question?.choices) {
    const choice = question.choices.find((c) => c.value === value);
    if (choice) return choice.label;
  }
  if (typeof value === 'boolean') return value ? 'Oui' : 'Non';
  return readableValue(value);
}

// Récapitulatif des données saisies, groupées par sous-étape du questionnaire.
const inputGroups = computed(() => {
  const groups = [];
  steps.forEach((step) => {
    if (step.type !== 'form' || !step.subSteps) return;
    step.subSteps.forEach((sub) => {
      const formName = sub.formName;
      if (!formName || !forms[formName]) return;
      const formData = simuData.value?.[formName];
      if (!formData) return;
      const items = [];
      (forms[formName].questions || []).forEach((varname) => {
        const question = questions.find((q) => q.varname === varname);
        const display = formatValue(question, formData[varname]);
        if (display === null) return;
        items.push({ label: question?.label ?? varname, value: display });
      });
      if (items.length) groups.push({ title: sub.title, items });
    });
  });
  return groups;
});

const dateStr = new Date().toLocaleDateString('fr-FR', {
  day: '2-digit',
  month: 'long',
  year: 'numeric',
});

const currentYear = new Date().getFullYear();
const versionString = `${process.env.VUE_APP_VERSION_NUM_MAJ}.${process.env.VUE_APP_VERSION_NUM_MIN}.${process.env.VUE_APP_VERSION_NUM_REV}`;

// Nom de fichier daté : YYYY-MM-DD_compte-rendu-RESPIRE
const now = new Date();
const fileDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(
  2,
  '0',
)}-${String(now.getDate()).padStart(2, '0')}`;
const fileName = `${fileDate}_compte-rendu-RESPIRE`;

const radarComp = ref(null);
const colComp = ref(null);
const radarPng = ref(null);
const colPng = ref(null);
const chartsReady = ref(false);

// Convertit les graphiques ApexCharts en PNG une fois qu'ils sont rendus.
async function generatePngs() {
  try {
    if (radarComp.value) {
      const { imgURI } = await radarComp.value.getPng();
      radarPng.value = imgURI;
    }
  } catch (e) {
    console.error('[ReportPrint] PNG radar', e);
  }
  try {
    if (colComp.value) {
      const { imgURI } = await colComp.value.getPng();
      colPng.value = imgURI;
    }
  } catch (e) {
    console.error('[ReportPrint] PNG histogramme', e);
  }
  chartsReady.value = true;
}

onMounted(() => {
  // Laisse le temps à ApexCharts de terminer son animation (~0,8s) avant l'export PNG.
  setTimeout(generatePngs, 1300);
});

const docRef = ref(null);
const busy = ref(false);

// === A : impression navigateur (window.print + CSS @media print) ===
// On passe le nom voulu au titre du document : le navigateur le propose comme
// nom de fichier dans la boîte « Enregistrer en PDF ».
function printReport() {
  matomoTrackEvent('Rapport', 'Imprimer');
  const previousTitle = document.title;
  document.title = fileName;
  const restore = () => {
    document.title = previousTitle;
    window.removeEventListener('afterprint', restore);
  };
  window.addEventListener('afterprint', restore);
  window.print();
}

// === B : html2pdf.js — capture du rapport HTML propre (images locales, pas de CORS) ===
async function downloadHtml2pdf() {
  if (!docRef.value) return;
  matomoTrackEvent('Rapport', 'Télécharger le PDF');
  busy.value = true;
  try {
    const html2pdf = (await import('html2pdf.js')).default;
    await html2pdf()
      .set({
        // [haut, gauche, bas, droite] en mm : marges hautes/basses sur CHAQUE page
        // (gauche/droite gérées par le padding interne du document)
        margin: [12, 0, 14, 0],
        filename: `${fileName}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, logging: false },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        pagebreak: {
          mode: ['css', 'legacy'],
          // saut de page avant chaque section (= avant chaque titre)
          before: ['.report-section'],
          // ne jamais couper au milieu de ces éléments
          avoid: [
            '.report-score',
            '.report-chart',
            '.accordion-item',
            '.recoTvx-bloc',
            '.report-footer',
          ],
        },
      })
      .from(docRef.value)
      .save();
  } catch (e) {
    console.error('[ReportPrint] html2pdf', e);
    alert('Erreur génération PDF (html2pdf) : ' + e.message);
  } finally {
    busy.value = false;
  }
}
</script>

<style scoped lang="scss">
.report-overlay {
  position: fixed;
  inset: 0;
  z-index: 2000;
  overflow: auto;
  background: #f2f2f2;
  padding: 1rem 1rem 4rem;
}

.report-toolbar {
  position: sticky;
  top: 0;
  z-index: 10;
  display: flex;
  gap: 0.5rem;
  justify-content: center;
  padding: 0.75rem;
  margin: -1rem -1rem 1rem;
  background: #fff;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
}

.report-doc {
  width: 210mm;
  max-width: 100%;
  margin: 0 auto;
  padding: 16mm 14mm;
  background: #fff;
  box-shadow: 0 0 12px rgba(0, 0, 0, 0.12);
  color: #222;
}

.report-cover {
  min-height: 240mm;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;

  // neutralise le picto "ouverture nouvelle fenêtre" ajouté par app.scss
  a[target='_blank']::after {
    content: none;
  }

  .report-cover-logos {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 3rem;
    margin-bottom: 3rem;
  }
  .report-cover-logo-respire {
    height: 110px;
    width: auto;
  }
  .report-cover-logo-lab {
    height: 80px;
    width: auto;
  }
  h1 {
    font-size: 2rem;
    margin: 0 0 0.6rem;
  }
  .report-subtitle {
    margin: 0;
    color: #666;
    font-size: 1.05rem;
  }
}

.report-section {
  margin-bottom: 1.8rem;
}

.report-h2 {
  font-size: 1.15rem;
  color: #0d6efd;
  border-left: 4px solid #fd7e14;
  padding-left: 0.6rem;
  margin-bottom: 0.8rem;
}

.report-h3 {
  font-size: 1rem;
  text-align: center;
  color: #444;
  margin: 1rem 0 0.4rem;
}

.report-note {
  font-size: 0.85rem;
  color: #555;
  margin-bottom: 0.8rem;

  // variante multi-paragraphes (<div class="report-note">)
  p {
    margin-bottom: 0.5rem;
  }
  p:last-child {
    margin-bottom: 0;
  }
}

.report-score {
  display: flex;
  justify-content: center;
}

.report-chart {
  text-align: center;

  img {
    max-width: 100%;
    height: auto;
  }
}

.report-chart-loading {
  color: #999;
  font-style: italic;
  padding: 2rem;
}

.report-days {
  text-align: center;
  font-size: 1.2rem;

  strong {
    font-size: 1.6rem;
    display: inline-block;
  }
  .report-days-sub {
    display: block;
    font-size: 0.8rem;
    color: #777;
  }
}

/* Accordéon "Points de vigilance" : toujours déplié et non interactif dans le rapport */
.report-alerts {
  margin-top: 1.2rem;

  :deep(.accordion-collapse) {
    display: block !important;
  }
  :deep(.accordion-button) {
    pointer-events: none;
    background-color: #f8f9fa;
    color: #222;
    box-shadow: none;
  }
  :deep(.accordion-button::after) {
    display: none;
  }
}

/* Recommandations : les styles d'origine sont imbriqués sous .recoTvxMain-liste
   (absent du rapport). On redéfinit ici la taille + couleur des icônes et un
   agencement propre des blocs. */
.report-reco {
  :deep(.recoTvx-liste dt) {
    font-weight: bold;
    color: #0d6efd;
    margin-top: 1rem;
    margin-bottom: 0.3rem;
  }
  :deep(.recoTvx-bloc) {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    padding: 0.6rem 0.2rem;
    border-bottom: 1px solid #eee;
  }
  :deep(.recoTvx-icon-wrapper) {
    flex: 0 0 auto;
  }
  :deep(.recoTvx-icon-item) {
    width: 30px;
    height: 30px;
    color: #fd7e14;
  }
  :deep(.recoTvx-title) {
    color: #0d6efd;
    font-weight: bold;
    margin-bottom: 0.2rem;
  }
}

.report-inputs {
  .report-input-group {
    margin-bottom: 1.2rem;
    page-break-inside: avoid;
  }
  .report-input-title {
    font-size: 1rem;
    font-weight: bold;
    color: #0d6efd;
    margin: 0.8rem 0 0.3rem;
  }
  .report-input-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.85rem;

    th,
    td {
      text-align: left;
      vertical-align: top;
      padding: 0.3rem 0.5rem;
      border-bottom: 1px solid #eee;
    }
    th {
      width: 58%;
      font-weight: normal;
      color: #555;
    }
    td {
      font-weight: bold;
      color: #222;
    }
  }
}

.report-footer {
  display: flex;
  align-items: center;
  gap: 1.2rem;
  margin-top: 2.5rem;
  padding-top: 1rem;
  border-top: 2px solid #fd7e14;

  // neutralise le picto "ouverture nouvelle fenêtre" ajouté par app.scss
  a[target='_blank']::after {
    content: none;
  }

  a {
    color: inherit;
  }
  // le lien enveloppe le logo : il devient l'item flex
  > a {
    flex: 0 0 auto;
  }

  .report-footer-logo {
    display: block;
    width: 150px;
    height: auto;
  }
  .report-footer-text {
    margin: 0;
    font-size: 0.72rem;
    color: #666;
    line-height: 1.4;
  }
}

.report-offscreen {
  position: absolute;
  left: -9999px;
  top: 0;
  width: 600px;
}
</style>

<!-- Style GLOBAL (non scoped) : impression du seul rapport -->
<style lang="scss">
@media print {
  @page {
    size: A4 portrait;
    margin: 12mm;
  }

  /* On masque l'application, on ne garde que l'overlay du rapport */
  #app {
    display: none !important;
  }

  .report-overlay {
    position: static !important;
    overflow: visible !important;
    background: #fff !important;
    padding: 0 !important;
  }
  .report-doc {
    width: auto !important;
    max-width: none !important;
    margin: 0 !important;
    padding: 0 !important;
    box-shadow: none !important;
  }

  .no-print {
    display: none !important;
  }

  /* saut de page avant chaque section (= avant chaque titre) */
  .report-section {
    page-break-before: always;
  }

  /* évite les coupures au milieu d'un bloc / graphique / reco */
  .report-section,
  .report-chart,
  .recoTvx-bloc,
  .report-input-group,
  .report-footer {
    page-break-inside: avoid;
  }

  /* conserve les couleurs de fond (encadrés, titres, barres...) */
  * {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
}
</style>
